import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { formatPriceDisplay } from '../../../../util/formatting';
import { type GroupedAssetRow } from '../utils/cartTransforms';
import { AssetServiceCell, DescriptionCell } from './AssetCartCells';

type AssetCartCardProps = {
  row: GroupedAssetRow;
  onEdit?: (row: GroupedAssetRow) => void;
  onDelete?: (row: GroupedAssetRow) => void;
  isMutating?: boolean;
  readOnly?: boolean;
  /** Mirrors the table's read-only Order ID column (confirmation page). */
  showOrderNumber?: boolean;
};

/**
 * Below-lg card for one grouped asset cart line. Reuses the table's cell
 * components (AssetServiceCell, DescriptionCell) so table and card stay one
 * source of truth — and shows every field the table shows.
 */
const AssetCartCard = ({
  row,
  onEdit,
  onDelete,
  isMutating = false,
  readOnly = false,
  showOrderNumber = false,
}: AssetCartCardProps) => {
  const removeLabel = `Remove ${row.service?.name || 'image edit'} from cart`;

  return (
    <Card
      className={cn(
        'flex flex-col gap-3 border-border p-4',
        isMutating && 'pointer-events-none opacity-60',
      )}
      data-testid="asset-cart-card"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {showOrderNumber && row.orderNumber && (
            <Typography
              variant="body-medium"
              className="mb-1 text-muted-foreground"
            >
              Order #{row.orderNumber}
            </Typography>
          )}
          <AssetServiceCell item={row} spreadOnHover={false} />
        </div>
        {!readOnly &&
          onDelete &&
          (isMutating ? (
            <Spinner size="sm" />
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 text-muted-foreground hover:text-primary"
              aria-label={removeLabel}
              onClick={() => onDelete(row)}
            >
              <Trash2 className="h-5 w-5" />
            </Button>
          ))}
      </div>

      <div>
        <Typography
          as="p"
          variant="small"
          className="mb-1 font-medium uppercase tracking-wide text-muted-foreground"
        >
          Description
        </Typography>
        <DescriptionCell row={row} onEdit={onEdit} readOnly={readOnly} />
      </div>

      <div className="flex items-center justify-between border-t border-border pt-3">
        {!readOnly ? (
          <Typography variant="body-medium" className="text-muted-foreground">
            Qty: <span className="text-foreground">{row.quantity}</span>
          </Typography>
        ) : (
          <Typography variant="body-semibold">Total</Typography>
        )}
        {!readOnly ? (
          <div className="flex items-baseline gap-1.5">
            <Typography variant="body-semibold">Total</Typography>
            <Typography variant="body-semibold">
              ${formatPriceDisplay(row.price)}
            </Typography>
          </div>
        ) : (
          <Typography variant="body-semibold">
            ${formatPriceDisplay(row.price)}
          </Typography>
        )}
      </div>
    </Card>
  );
};

export default AssetCartCard;
